import axiosInstance from 'shared/services/api/axiosInstance';

/**
 * GET with in-flight de-duplication and a very short result cache.
 *
 * The analytics list pages each issue their main query 2-3 times per load. Two
 * causes, neither of which the call site can see:
 *   - React.StrictMode double-invokes effects in development.
 *   - The loader is a useCallback over filter/range state; when any of those
 *     identities churn on mount, the effect fires again.
 *
 * Every one of those is the SAME url+params, so collapsing them here fixes it
 * for every caller at once — no page has to restructure its dependencies, and
 * no behaviour changes. Requests that genuinely differ (a new page offset, a
 * changed filter, another time window) have a different key and are untouched.
 *
 * The TTL is deliberately tiny: long enough to absorb the mount-time churn,
 * short enough that these lists never feel stale. Use `invalidateGet()` after a
 * mutation if a caller needs to force the next read through.
 */
const DEFAULT_TTL_MS = 4000;
const MAX_ENTRIES = 120;

const cache = new Map(); // key -> { at, promise }

function keyOf(url, params) {
  // Sort keys so param order can never split the cache.
  const p = params && typeof params === 'object'
    ? Object.keys(params).sort().map((k) => [k, params[k]])
    : null;
  return `${url}::${JSON.stringify(p)}`;
}

function prune() {
  if (cache.size <= MAX_ENTRIES) return;
  const oldest = cache.keys().next().value;
  if (oldest !== undefined) cache.delete(oldest);
}

/**
 * @param {string} url
 * @param {object} [config]        axios config; only `params` participates in the key
 * @param {number} [config.ttlMs]  override the cache window
 * @returns {Promise<any>} the axios response
 */
export function dedupedGet(url, config = {}) {
  const { ttlMs = DEFAULT_TTL_MS, ...axiosConfig } = config;
  // A caller passing an abort signal wants to own the request's lifetime;
  // sharing a promise across callers would let one cancel another's data.
  if (axiosConfig.signal) return axiosInstance.get(url, axiosConfig);

  const key = keyOf(url, axiosConfig.params);
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at <= ttlMs) return hit.promise;

  const promise = axiosInstance.get(url, axiosConfig);
  cache.set(key, { at: Date.now(), promise });
  // A failure must not be replayed for the rest of the window.
  promise.catch(() => cache.delete(key));
  prune();
  return promise;
}

/** Drop cached GETs — all of them, or those whose url contains `match`. */
export function invalidateGet(match) {
  if (!match) { cache.clear(); return; }
  for (const k of Array.from(cache.keys())) {
    if (k.includes(match)) cache.delete(k);
  }
}

export default dedupedGet;
